import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseDialogAction } from '../../../components/shared/dialogs/CloseDialogAction';
import { CreatePurchaseOrderHeaderRequest } from '../../../app/services/api/generated';
import { generateAdHocEwsOrderNumber, generateAdHocOrderIdentifiers } from '../utils/adHocOrderNumbers';

interface CreateAdHocReceiptModalProps {
  open: boolean;
  isSubmitting: boolean;
  storeNumber: string;
  onClose: () => void;
  onSubmit: (payload: CreatePurchaseOrderHeaderRequest) => Promise<void>;
}

const CreateAdHocReceiptModal = ({
  open,
  isSubmitting,
  storeNumber,
  onClose,
  onSubmit,
}: CreateAdHocReceiptModalProps) => {
  const { t } = useTranslation('common');

  const [supplierName, setSupplierName] = useState('');
  const [vendorAccountNumber, setVendorAccountNumber] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [ewsOrderNumber, setEwsOrderNumber] = useState('');
  const [errors, setErrors] = useState<string[]>([]);
  const createdAtRef = useRef<Date>(new Date());

  useEffect(() => {
    if (!open) {
      return;
    }

    createdAtRef.current = new Date();
    const generatedIdentifiers = generateAdHocOrderIdentifiers(storeNumber, createdAtRef.current);

    setSupplierName('');
    setVendorAccountNumber('');
    setDeliveryDate('');
    setOrderNumber(generatedIdentifiers.orderNumber);
    setEwsOrderNumber(generatedIdentifiers.ewsOrderNumber);
    setErrors([]);
  }, [open, storeNumber]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setEwsOrderNumber(generateAdHocEwsOrderNumber(storeNumber, supplierName, createdAtRef.current));
  }, [open, storeNumber, supplierName]);

  const validate = (): string[] => {
    const validationErrors: string[] = [];

    if (!storeNumber) {
      validationErrors.push(t('purchaseOrderReceipt.adHocReceiptModal.validation.storeRequired'));
    }

    if (!supplierName.trim() && !vendorAccountNumber.trim()) {
      validationErrors.push(t('purchaseOrderReceipt.adHocReceiptModal.validation.supplierOrVendorRequired'));
    }

    if (!deliveryDate) {
      validationErrors.push(t('purchaseOrderReceipt.adHocReceiptModal.validation.deliveryDateRequired'));
    }

    return validationErrors;
  };

  const submit = async () => {
    const validationErrors = validate();
    setErrors(validationErrors);

    if (validationErrors.length > 0) {
      return;
    }

    const payload: CreatePurchaseOrderHeaderRequest = {
      storeNumber,
      supplierName: supplierName.trim() || undefined,
      vendorAccountNumber: vendorAccountNumber.trim() || undefined,
      deliveryDate,
      orderNumber: orderNumber.trim(),
      ewsOrderNumber: ewsOrderNumber.trim(),
    };

    await onSubmit(payload);
  };

  return (
    <Dialog
      onClose={onClose}
      maxWidth="md"
      fullWidth
      open={open}
    >
      <DialogTitle>
        {t('purchaseOrderReceipt.adHocReceiptModal.title')}
        <CloseDialogAction onCancel={onClose} />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}
          sx={{ pt: 1 }}>
          {errors.length > 0 && (
            <Box>
              {errors.map((error) => (
                <Typography key={error}
                  color="error"
                  variant="textSM">
                  {error}
                </Typography>
              ))}
            </Box>
          )}

          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={6}>
              <TextField
                fullWidth
                label={t('purchaseOrderReceipt.adHocReceiptModal.orderNumber')}
                value={orderNumber}
                inputProps={{ maxLength: 30 }}
                onChange={(event) => setOrderNumber(event.target.value)}
              />
            </Grid>
            <Grid item
              xs={12}
              md={6}>
              <TextField
                fullWidth
                label={t('purchaseOrderReceipt.adHocReceiptModal.storeNumber')}
                value={storeNumber}
                disabled
              />
            </Grid>
            <Grid item
              xs={12}
              md={6}>
              <TextField
                fullWidth
                label={t('purchaseOrderReceipt.adHocReceiptModal.supplierName')}
                value={supplierName}
                onChange={(event) => setSupplierName(event.target.value)}
              />
            </Grid>
            <Grid item
              xs={12}
              md={6}>
              <TextField
                fullWidth
                label={t('purchaseOrderReceipt.adHocReceiptModal.vendorAccountNumber')}
                value={vendorAccountNumber}
                onChange={(event) => setVendorAccountNumber(event.target.value)}
              />
            </Grid>
            <Grid item
              xs={12}
              md={6}>
              <TextField
                fullWidth
                type="date"
                label={t('purchaseOrderReceipt.adHocReceiptModal.deliveryDate')}
                InputLabelProps={{ shrink: true }}
                value={deliveryDate}
                onChange={(event) => setDeliveryDate(event.target.value)}
              />
            </Grid>
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}>
          {t('cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={submit}
          disabled={isSubmitting}>
          {t('purchaseOrderReceipt.adHocReceiptModal.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAdHocReceiptModal;
